import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Globe2, Loader2, Megaphone, Sparkles, Wand2 } from "lucide-react";

import { Button } from "@/ui-components/ui/button";
import { Card, CardContent, CardHeader } from "@/ui-components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Switch } from "@/ui-components/ui/switch";
import { Textarea } from "@/ui-components/ui/textarea";
import { Skeleton } from "@/ui-components/ui/skeleton";
import { SubPageLayout } from "@/util-components/page-layout/sub-page-layout";
import { SectionHeader } from "@/util-components/page-layout/section-header";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { usePermissions } from "@/lib/hooks/use-permissions";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils/cn";
import { Context } from "@/util-components/root/context";
import { DepartmentAiCaptionStylePreset } from "@/lib/api/interfaces/organizations.interface";
import {
  useOrganizationsDepartmentAiCaptionSettingsQuery,
  useOrganizationsUpdateDepartmentAiCaptionSettings,
} from "@/lib/api/requests/organizations.requests";

const STYLE_OPTIONS: Array<{
  value: DepartmentAiCaptionStylePreset;
  title: string;
  description: string;
  badge: string;
  icon: typeof Wand2;
}> = [
  {
    value: DepartmentAiCaptionStylePreset.CLUB_GERMAN,
    title: "Klar auf Deutsch",
    description: "Sachlich, nahbar und passend für offizielle Vereinskommunikation.",
    badge: "Standard",
    icon: Wand2,
  },
  {
    value: DepartmentAiCaptionStylePreset.HYPE_GERMAN,
    title: "Emotional auf Deutsch",
    description: "Mehr Energie, Community-Vibes und ein lebendigerer Ton.",
    badge: "Dynamik",
    icon: Megaphone,
  },
  {
    value: DepartmentAiCaptionStylePreset.INTERNATIONAL_ENGLISH,
    title: "International",
    description: "Sauberer englischer Stil für internationale Reichweite.",
    badge: "English",
    icon: Globe2,
  },
];

const aiCaptionSettingsSchema = z.object({
  enabled: z.boolean(),
  stylePreset: z.nativeEnum(DepartmentAiCaptionStylePreset),
  customInstructions: z
    .string()
    .max(1200, "Bitte maximal 1200 Zeichen verwenden."),
});

type AiCaptionSettingsValues = z.infer<typeof aiCaptionSettingsSchema>;

const defaultValues: AiCaptionSettingsValues = {
  enabled: false,
  stylePreset: DepartmentAiCaptionStylePreset.CLUB_GERMAN,
  customInstructions: "",
};

const getResetValues = (
  data?: {
    enabled: boolean;
    stylePreset: DepartmentAiCaptionStylePreset;
    customInstructions?: string | null;
  },
): AiCaptionSettingsValues => ({
  enabled: data?.enabled ?? false,
  stylePreset: data?.stylePreset ?? DepartmentAiCaptionStylePreset.CLUB_GERMAN,
  customInstructions: data?.customInstructions ?? "",
});

type StyleOptionCardProps = {
  option: (typeof STYLE_OPTIONS)[number];
  isActive: boolean;
  disabled: boolean;
  onSelect: (value: DepartmentAiCaptionStylePreset) => void;
};

const StyleOptionCard = ({ option, isActive, disabled, onSelect }: StyleOptionCardProps) => {
  const Icon = option.icon;

  return (
    <button
      type="button"
      onClick={() => onSelect(option.value)}
      disabled={disabled}
      className={cn(
        "group relative flex flex-col gap-4 rounded-2xl border p-5 text-left transition-all duration-200",
        isActive
          ? "border-blue-600 bg-blue-50/30 ring-4 ring-blue-500/10 shadow-sm"
          : "border-gray-200 bg-white hover:border-gray-300",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors",
            isActive 
              ? "border-blue-100 bg-blue-50 text-blue-600" 
              : "border-gray-100 bg-gray-50 text-gray-400 group-hover:border-gray-200 group-hover:text-gray-500",
          )}
        >
          <Icon className="h-5 w-5" />
        </div>

        <span
          className={cn(
            "inline-flex h-6 items-center rounded-full px-2.5 text-[10px] font-bold uppercase tracking-wider",
            isActive ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-500",
          )}
        >
          {option.badge}
        </span>
      </div>

      <div className="space-y-1">
        <div className={cn(
          "text-sm font-bold transition-colors",
          isActive ? "text-blue-900" : "text-gray-900"
        )}>
          {option.title}
        </div>
        <div className="text-xs leading-relaxed text-gray-500">
          {option.description}
        </div>
      </div>
    </button>
  );
};

const OrganizationAiCaptionSettingsSkeleton = () => {
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-4 sm:gap-6 sm:px-6 sm:py-8">
      <Card className="overflow-hidden border-gray-200 shadow-sm">
        <CardHeader className="border-b border-gray-100 bg-gradient-to-br from-gray-50 via-white to-gray-50/60 px-5 py-6 sm:px-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-11 w-11 rounded-2xl" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-40 rounded-lg" />
              <Skeleton className="h-4 w-72 rounded-lg" />
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-8 p-5 sm:p-6">
          <div className="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-gray-50/30 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="space-y-2">
              <Skeleton className="h-5 w-48 rounded-lg" />
              <Skeleton className="h-4 w-full max-w-xs rounded-lg" />
            </div>
            <Skeleton className="h-6 w-11 rounded-full" />
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Skeleton className="h-5 w-32 rounded-lg" />
              <Skeleton className="h-4 w-56 rounded-lg" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl border border-gray-100 bg-white p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-10 w-10 rounded-xl" />
                    <Skeleton className="h-6 w-16 rounded-full" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-24 rounded-lg" />
                    <Skeleton className="h-3 w-full rounded-lg" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <Skeleton className="h-5 w-40 rounded-lg" />
            <Skeleton className="h-32 w-full rounded-2xl" />
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-gray-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <Skeleton className="h-10 w-full rounded-xl sm:w-32" />
            <Skeleton className="h-10 w-full rounded-xl sm:w-44" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const OrganizationAiCaptionSettings = () => {
  const navigate = useNavigate();
  const { currentDepartment } = useContext(Context);
  const { can } = usePermissions();
  const canUseAiCaptions = can(Permission.GENERATE_AI_CAPTIONS);

  const { data, isLoading } = useOrganizationsDepartmentAiCaptionSettingsQuery({
    enabled: canUseAiCaptions,
  });
  const updateSettings = useOrganizationsUpdateDepartmentAiCaptionSettings();

  const form = useForm<AiCaptionSettingsValues>({
    resolver: zodResolver(aiCaptionSettingsSchema),
    defaultValues,
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    form.reset(getResetValues(data));
  }, [data, form]);

  const onSubmit = async (values: AiCaptionSettingsValues) => {
    try {
      await updateSettings.mutateAsync({
        enabled: values.enabled,
        stylePreset: values.stylePreset,
        customInstructions: values.customInstructions.trim(),
      });
      toast.success("KI-Anpassung gespeichert.");
    } catch (error) {
      toast.error("KI-Anpassung konnte nicht gespeichert werden.", { error });
    }
  };

  if (isLoading && canUseAiCaptions) {
    return (
      <SubPageLayout
        pageTitle="KI Anpassung"
        parentPageTitle="Dein Verein"
        parentPageCallback={() => navigate("/vereine")}
        navigateBackCallback={() => navigate("/vereine")}
        icon={<Sparkles className="h-6 w-6" />}
      >
        <OrganizationAiCaptionSettingsSkeleton />
      </SubPageLayout>
    );
  }

  return (
    <SubPageLayout
      pageTitle="KI Anpassung"
      parentPageTitle="Dein Verein"
      parentPageCallback={() => navigate("/vereine")}
      navigateBackCallback={() => navigate("/vereine")}
      icon={<Sparkles className="h-6 w-6" />}
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-4 sm:gap-6 sm:px-6 sm:py-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
            <Card className="overflow-hidden border-gray-200 shadow-sm transition-all">
              <CardHeader className="space-y-5 border-b border-gray-100 bg-gradient-to-br from-gray-50 via-white to-gray-50/60 px-5 py-5 sm:px-6">
                <SectionHeader
                  icon={Wand2}
                  title="KI-Bildunterschrift"
                  description={`Konfiguriere den KI-Stil für ${currentDepartment || data?.department || "deine Abteilung"}`}
                />
              </CardHeader>

              <CardContent className="space-y-8 p-5 sm:p-6">
                <FormField
                  control={form.control}
                  name="enabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-gray-50/30 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                      <div className="space-y-1">
                        <FormLabel className="text-sm font-semibold text-gray-900 sm:text-base">
                          Automatische Generierung
                        </FormLabel>
                        <p className="text-xs text-gray-500 sm:text-sm">
                          Erzeugt beim Erstellen eines Posts automatisch einen ersten Vorschlag.
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!canUseAiCaptions}
                          className="self-start sm:self-center"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="stylePreset"
                  render={({ field }) => (
                    <FormItem className="space-y-4">
                      <div className="space-y-1">
                        <FormLabel className="text-base font-semibold text-gray-900">
                          Tonalität & Sprache
                        </FormLabel>
                        <p className="text-sm text-gray-500">
                          Wähle die Grundstimmung der generierten Texte.
                        </p>
                      </div>
                      <FormControl>
                        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                          {STYLE_OPTIONS.map((option) => (
                            <StyleOptionCard
                              key={option.value}
                              option={option}
                              isActive={field.value === option.value}
                              disabled={!canUseAiCaptions}
                              onSelect={field.onChange}
                            />
                          ))}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="customInstructions"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <div className="space-y-1">
                        <FormLabel className="text-base font-semibold text-gray-900">
                          Individuelle Vorgaben
                        </FormLabel>
                        <p className="text-sm text-gray-500">
                          Ergänze spezifische Details wie Hashtags oder Sponsoren.
                        </p>
                      </div>
                      <FormControl>
                        <Textarea
                          {...field}
                          rows={4}
                          disabled={!canUseAiCaptions}
                          placeholder="z.B. Mannschafts-Spitzname, feste Hashtags oder Tonalitätshinweise..."
                          className="min-h-[112px] resize-none rounded-2xl border-gray-200 bg-white p-4 text-sm shadow-sm transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 sm:min-h-[120px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!canUseAiCaptions && (
                  <div className="flex items-start gap-3 rounded-2xl border border-amber-100 bg-amber-50/50 p-4 text-sm text-amber-800">
                    <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                    <span>KI-Captions sind im Pro- oder Custom-Plan enthalten.</span>
                  </div>
                )}

                <div className="flex flex-col-reverse gap-3 border-t border-gray-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-10 w-full rounded-xl px-4 text-sm font-semibold text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 sm:w-auto"
                    onClick={() => form.reset(getResetValues(data))}
                    disabled={isLoading || updateSettings.isPending || !form.formState.isDirty}
                  >
                    <Loader2 className="mr-2 h-4 w-4 opacity-0 transition-opacity" />
                    Änderungen verwerfen
                  </Button>

                  <div className="flex w-full sm:w-auto">
                    <PermissionFeatureWrapper
                      requiredPermission={Permission.GENERATE_AI_CAPTIONS}
                      requiredPlan={PlanKey.PRO}
                    >
                      <Button
                        type="submit"
                        disabled={isLoading || updateSettings.isPending || !form.formState.isDirty}
                        className={cn(
                          "h-10 w-full rounded-xl px-6 text-sm font-bold text-white shadow-md shadow-blue-500/20 transition-all",
                          "bg-blue-600 hover:bg-blue-700 hover:shadow-lg active:scale-[0.98] disabled:opacity-50 sm:w-auto",
                        )}
                      >
                        {updateSettings.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Speichern...
                          </>
                        ) : (
                          "Einstellungen speichern"
                        )}
                      </Button>
                    </PermissionFeatureWrapper>
                  </div>
                </div>
              </CardContent>
            </Card>
          </form>
        </Form>
      </div>
    </SubPageLayout>
  );
};
